'use client';

import type { TableDisplayProps } from '@/@studio-ai/props';
import { canExportTable, copyTableToClipboard } from '@/@studio-ai/utils/table-export-utils';
import { Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@heroui/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { ContentWrapper } from './content-wrapper';
import { Markdown } from './markdown';

export function TableDisplay({
    tableMarkdown,
    collapsible = false,
    title = 'View result table',
    className = '',
    onExportFullData,
}: TableDisplayProps) {
    const content = (
        <div className={`text-small ${className}`}>
            <Markdown>{tableMarkdown}</Markdown>
        </div>
    );

    const canExport = canExportTable(tableMarkdown);

    const handleCopyTable = async () => {
        try {
            await copyTableToClipboard(tableMarkdown);
            toast.success('Table copied');
        } catch {
            toast.error('Could not copy the table. Use Download full data instead.');
        }
    };

    return (
        <ContentWrapper className="group relative">
            {collapsible ? (
                <details open={false}>
                    <summary className="mb-2 cursor-pointer text-mini font-medium text-primary hover:text-primary-600">
                        {title}
                    </summary>
                    <div className="mt-2">{content}</div>
                </details>
            ) : (
                content
            )}

            <div className="absolute right-2 top-3 z-50 rounded-md border bg-background opacity-0 shadow-small backdrop-blur-sm group-hover:opacity-100">
                <Dropdown placement="bottom-end">
                    <DropdownTrigger>
                        <div className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md opacity-70 transition-opacity hover:bg-default-200 hover:opacity-100">
                            <Icon icon="lucide:more-vertical" width={14} />
                        </div>
                    </DropdownTrigger>
                    <DropdownMenu
                        disabledKeys={!canExport && !onExportFullData ? ['export-xlsx'] : []}
                    >
                        <DropdownItem
                            key="copy-table"
                            color="primary"
                            startContent={<Icon icon="lucide:clipboard" width={14} />}
                            onPress={handleCopyTable}
                        >
                            Copy table
                        </DropdownItem>
                        <DropdownItem
                            key="export-xlsx"
                            color="primary"
                            startContent={<Icon icon="lucide:sheet" width={14} />}
                            onPress={onExportFullData}
                        >
                            Download full data
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>
            </div>
        </ContentWrapper>
    );
}
